/**
 * Internal dependencies
 */
import type {
  BaseElement,
  Element,
  TextBoxAttachment,
  TextBoxAttachmentAlignment,
  TextBoxAttachmentPosition,
} from '../elements/types';

export type TextBoxAttachmentValidationError =
  | 'missing-dependent'
  | 'missing-target'
  | 'unsupported-dependent'
  | 'background-target'
  | 'self-reference'
  | 'cycle'
  | 'target-already-attached';

type NormalizeOptions = {
  rebaseIds?: Iterable<string>;
  resolve?: boolean;
  previousElements?: Element[];
};

const ALIGNMENTS: TextBoxAttachmentAlignment[] = ['start', 'center', 'end'];

type TextBoxAttachmentElement = Pick<
  BaseElement,
  'id' | 'x' | 'y' | 'width' | 'height' | 'rotationAngle'
>;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isBackgroundElement = (element: Element | undefined) =>
  Boolean(
    element && 'isBackground' in element && element.isBackground === true
  );

export const isTextBoxAttachmentDependent = (element: Element | undefined) =>
  Boolean(
    element &&
      ['text', 'image', 'shape', 'video', 'sticker'].includes(element.type) &&
      !isBackgroundElement(element)
  );

const TEXT_BOX_ATTACHMENT_TRANSFORM_PREFIX = 'text-box-attachment:';

export const getTextBoxAttachmentTransformId = (targetId: string) =>
  `${TEXT_BOX_ATTACHMENT_TRANSFORM_PREFIX}${targetId}`;

const withoutTextBoxAttachment = <T extends Element>(element: T): T => {
  if (!('textBoxAttachment' in element)) {
    return element;
  }
  const { textBoxAttachment: _textBoxAttachment, ...rest } = element;
  return rest as T;
};

const getAlignmentX = (
  width: number,
  alignment: TextBoxAttachmentAlignment
) => {
  switch (alignment) {
    case 'start':
      return 0;
    case 'end':
      return width;
    case 'center':
    default:
      return width / 2;
  }
};

const rotateVector = (x: number, y: number, rotationAngle = 0) => {
  const radians = (rotationAngle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
};

const getWorldPoint = (
  element: TextBoxAttachmentElement,
  localX: number,
  localY: number
) => {
  const centerX = element.x + element.width / 2;
  const centerY = element.y + element.height / 2;
  const rotated = rotateVector(
    localX - element.width / 2,
    localY - element.height / 2,
    element.rotationAngle
  );
  return {
    x: centerX + rotated.x,
    y: centerY + rotated.y,
  };
};

const getTargetAnchor = (
  target: TextBoxAttachmentElement,
  alignment: TextBoxAttachmentAlignment,
  crossAxisOffset = 0,
  edge: 'top' | 'bottom' = 'top'
) =>
  getWorldPoint(
    target,
    getAlignmentX(target.width, alignment) + crossAxisOffset,
    edge === 'bottom' ? target.height : 0
  );

const getDependentAnchor = (
  dependent: TextBoxAttachmentElement,
  alignment: TextBoxAttachmentAlignment,
  edge: 'top' | 'bottom' = 'bottom'
) =>
  getWorldPoint(
    dependent,
    getAlignmentX(dependent.width, alignment),
    edge === 'bottom' ? dependent.height : 0
  );

export const isTextBoxAttachment = (
  value: unknown
): value is TextBoxAttachment => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const attachment = value as Partial<TextBoxAttachment>;
  return (
    (((attachment.version === 1 || attachment.version === 2) &&
      attachment.targetEdge === 'top' &&
      attachment.ownEdge === 'bottom') ||
      (attachment.version === 3 &&
        attachment.targetEdge === 'bottom' &&
        attachment.ownEdge === 'top')) &&
    typeof attachment.targetId === 'string' &&
    attachment.targetId.length > 0 &&
    ALIGNMENTS.includes(attachment.alignment as TextBoxAttachmentAlignment) &&
    isFiniteNumber(attachment.gap) &&
    (attachment.version !== 1 || attachment.gap >= 0) &&
    isFiniteNumber(attachment.crossAxisOffset) &&
    attachment.coordinateSpace === 'target-local'
  );
};

export const getTextBoxAttachmentPosition = (
  attachment?: TextBoxAttachment | null
): TextBoxAttachmentPosition =>
  attachment?.targetEdge === 'bottom' ? 'below' : 'above';

export const isTextBoxAttachmentTarget = (
  elements: Element[],
  targetId: string
) =>
  elements.some(
    (element) =>
      isTextBoxAttachmentDependent(element) &&
      isTextBoxAttachment(element.textBoxAttachment) &&
      element.textBoxAttachment.targetId === targetId
  );

export const getTextBoxAttachmentTargetTransformId = (
  elements: Element[],
  targetId: string
) =>
  isTextBoxAttachmentTarget(elements, targetId)
    ? getTextBoxAttachmentTransformId(targetId)
    : null;

const deriveForAlignment = (
  dependent: TextBoxAttachmentElement,
  target: TextBoxAttachmentElement,
  alignment: TextBoxAttachmentAlignment,
  position: TextBoxAttachmentPosition
): TextBoxAttachment => {
  const below = position === 'below';
  const targetAnchor = getTargetAnchor(
    target,
    alignment,
    0,
    below ? 'bottom' : 'top'
  );
  const dependentAnchor = getDependentAnchor(
    dependent,
    alignment,
    below ? 'top' : 'bottom'
  );
  const delta = {
    x: dependentAnchor.x - targetAnchor.x,
    y: dependentAnchor.y - targetAnchor.y,
  };
  const targetLocalDelta = rotateVector(
    delta.x,
    delta.y,
    -target.rotationAngle
  );
  const gap =
    targetLocalDelta.y === 0 ? 0 : targetLocalDelta.y * (below ? 1 : -1);

  return {
    // Keep Above pins readable by existing clients; Below needs the new pair.
    ...(below
      ? {
          version: 3 as const,
          targetEdge: 'bottom' as const,
          ownEdge: 'top' as const,
        }
      : {
          version: gap < 0 ? (2 as const) : (1 as const),
          targetEdge: 'top' as const,
          ownEdge: 'bottom' as const,
        }),
    targetId: target.id,
    alignment,
    gap,
    crossAxisOffset: targetLocalDelta.x,
    coordinateSpace: 'target-local',
  };
};

export const deriveTextBoxAttachment = (
  dependent: TextBoxAttachmentElement,
  target: TextBoxAttachmentElement,
  alignment?: TextBoxAttachmentAlignment,
  position: TextBoxAttachmentPosition = 'above'
): TextBoxAttachment => {
  if (alignment) {
    return deriveForAlignment(dependent, target, alignment, position);
  }

  return ALIGNMENTS.map((candidate) =>
    deriveForAlignment(dependent, target, candidate, position)
  ).reduce((closest, candidate) =>
    Math.abs(candidate.crossAxisOffset) < Math.abs(closest.crossAxisOffset)
      ? candidate
      : closest
  );
};

export const resolveTextBoxAttachment = <T extends BaseElement>(
  dependent: T,
  target: TextBoxAttachmentElement,
  attachment: TextBoxAttachment = dependent.textBoxAttachment as TextBoxAttachment
): T => {
  const targetAnchor = getTargetAnchor(
    target,
    attachment.alignment,
    attachment.crossAxisOffset,
    attachment.targetEdge
  );
  const gapOffset = rotateVector(
    0,
    attachment.gap * (attachment.targetEdge === 'bottom' ? 1 : -1),
    target.rotationAngle
  );
  const desiredAnchor = {
    x: targetAnchor.x + gapOffset.x,
    y: targetAnchor.y + gapOffset.y,
  };
  const currentAnchor = getDependentAnchor(
    dependent,
    attachment.alignment,
    attachment.ownEdge
  );
  const x = dependent.x + desiredAnchor.x - currentAnchor.x;
  const y = dependent.y + desiredAnchor.y - currentAnchor.y;

  if (Math.abs(x - dependent.x) < 1e-9 && Math.abs(y - dependent.y) < 1e-9) {
    return dependent;
  }

  return { ...dependent, x, y };
};

export const getTextBoxAttachmentValidationError = (
  elements: Element[],
  dependentId: string,
  targetId: string
): TextBoxAttachmentValidationError | null => {
  const elementById = new Map(elements.map((element) => [element.id, element]));
  const dependent = elementById.get(dependentId);
  const target = elementById.get(targetId);

  if (!dependent) {
    return 'missing-dependent';
  }
  if (!target) {
    return 'missing-target';
  }
  if (!isTextBoxAttachmentDependent(dependent)) {
    return 'unsupported-dependent';
  }
  if (isBackgroundElement(target)) {
    return 'background-target';
  }
  if (dependentId === targetId) {
    return 'self-reference';
  }
  // Following the proposed anchor must never lead back to this layer (or
  // into an already malformed cycle). Retargeting replaces the old link.
  const visited = new Set([dependentId]);
  let ancestor: Element | undefined = target;
  while (ancestor) {
    if (visited.has(ancestor.id)) {
      return 'cycle';
    }
    visited.add(ancestor.id);
    ancestor = isTextBoxAttachment(ancestor.textBoxAttachment)
      ? elementById.get(ancestor.textBoxAttachment.targetId)
      : undefined;
  }
  if (
    elements.some(
      (element) =>
        element.id !== dependentId &&
        isTextBoxAttachmentDependent(element) &&
        isTextBoxAttachment(element.textBoxAttachment) &&
        element.textBoxAttachment.targetId === targetId
    )
  ) {
    return 'target-already-attached';
  }
  return null;
};

export const canAttachTextBox = (
  elements: Element[],
  dependentId: string,
  targetId: string
) => !getTextBoxAttachmentValidationError(elements, dependentId, targetId);

// Membership uses the existing one-child chain, not drawing order or groups.
export const getTextBoxAttachmentStack = (
  elements: Element[],
  elementId: string
): Element[] => {
  const byId = new Map(elements.map((element) => [element.id, element]));
  let root = byId.get(elementId);
  const visited = new Set<string>();
  while (root && isTextBoxAttachment(root.textBoxAttachment)) {
    if (visited.has(root.id)) return [];
    visited.add(root.id);
    root = byId.get(root.textBoxAttachment.targetId);
  }
  if (!root) return [];
  const childByTarget = new Map(
    elements
      .filter((element) => isTextBoxAttachment(element.textBoxAttachment))
      .map((element) => [element.textBoxAttachment!.targetId, element])
  );
  const stack: Element[] = [];
  visited.clear();
  let member: Element | undefined = root;
  while (member && !visited.has(member.id)) {
    visited.add(member.id);
    stack.push(member);
    member = childByTarget.get(member.id);
  }
  return stack;
};

type GrowthAnchor = 'top' | 'center' | 'bottom';

const getStackGrowthPosition = (
  stack: Element[],
  rotationAngle: number,
  anchor: GrowthAnchor
) => {
  const positions = stack.flatMap((element) =>
    [0, element.width].flatMap((x) =>
      [0, element.height].map((y) => {
        const point = getWorldPoint(element, x, y);
        return rotateVector(point.x, point.y, -rotationAngle).y;
      })
    )
  );
  const top = Math.min(...positions);
  const bottom = Math.max(...positions);
  return anchor === 'top'
    ? top
    : anchor === 'bottom'
    ? bottom
    : (top + bottom) / 2;
};

const reflowTextBoxAttachmentStacks = (
  elements: Element[],
  previousElements: Element[]
): Element[] => {
  const previousById = new Map(
    previousElements.map((element) => [element.id, element])
  );
  const shiftedById = new Map<string, Element>();
  const changedStacks = new Map<string, Element[]>();
  for (const element of elements) {
    if (
      element.type !== 'text' ||
      element.height === previousById.get(element.id)?.height
    )
      continue;
    const stack = getTextBoxAttachmentStack(elements, element.id);
    if (stack.length > 1) changedStacks.set(stack[0].id, stack);
  }
  for (const stack of Array.from(changedStacks.values())) {
    const root = stack[0];
    const previousStack = getTextBoxAttachmentStack(previousElements, root.id);
    if (
      previousStack.length !== stack.length ||
      !stack.every((element, index) => {
        const previous = previousStack[index];
        return (
          previous?.id === element.id &&
          previous.rotationAngle === element.rotationAngle &&
          (
            [
              'version',
              'targetId',
              'targetEdge',
              'ownEdge',
              'alignment',
              'gap',
              'crossAxisOffset',
              'coordinateSpace',
            ] as const
          ).every(
            (key) =>
              previous.textBoxAttachment?.[key] ===
              element.textBoxAttachment?.[key]
          )
        );
      })
    )
      continue;
    // Use the layer whose text reflowed, not an unchanged ancestor's setting.
    // Batched reflows use the first changed text member in root-first order.
    const changed = stack.find(
      (element) =>
        element.type === 'text' &&
        element.height !== previousById.get(element.id)?.height
    );
    if (!changed || !('verticalAnchor' in changed)) continue;
    const anchor = changed.verticalAnchor;
    if (anchor !== 'top' && anchor !== 'center' && anchor !== 'bottom')
      continue;
    const shift =
      getStackGrowthPosition(previousStack, root.rotationAngle, anchor) -
      getStackGrowthPosition(stack, root.rotationAngle, anchor);
    if (Math.abs(shift) < 1e-9) continue;
    const offset = rotateVector(0, shift, root.rotationAngle);
    stack.forEach((element) =>
      shiftedById.set(element.id, {
        ...element,
        x: element.x + offset.x,
        y: element.y + offset.y,
      })
    );
  }
  return shiftedById.size
    ? elements.map((element) => shiftedById.get(element.id) || element)
    : elements;
};

export const normalizeTextBoxAttachments = (
  elements: Element[],
  { rebaseIds = [], resolve = true, previousElements }: NormalizeOptions = {}
): Element[] => {
  const rebaseIdSet = new Set(rebaseIds);
  const elementById = new Map(elements.map((element) => [element.id, element]));

  const dependentByTarget = new Map<string, Element>();
  const candidates = elements.map((element) => {
    if (!('textBoxAttachment' in element)) {
      return element;
    }
    if (
      !isTextBoxAttachmentDependent(element) ||
      !isTextBoxAttachment(element.textBoxAttachment)
    ) {
      return withoutTextBoxAttachment(element);
    }
    const target = elementById.get(element.textBoxAttachment.targetId);
    if (
      !target ||
      isBackgroundElement(target) ||
      target.id === element.id ||
      dependentByTarget.has(target.id)
    ) {
      return withoutTextBoxAttachment(element);
    }
    dependentByTarget.set(target.id, element);
    return element;
  });

  // Each anchor has at most one child. Walk outward from every unpinned root,
  // using the resolved parent, rather than the original page geometry.
  const resolvedById = new Map<string, Element>();
  const pending = candidates.filter(
    (element) => !isTextBoxAttachment(element.textBoxAttachment)
  );
  for (let index = 0; index < pending.length; index++) {
    const element = pending[index];
    resolvedById.set(element.id, element);
    const child = dependentByTarget.get(element.id);
    if (!child || !isTextBoxAttachment(child.textBoxAttachment)) {
      continue;
    }
    const attachment = rebaseIdSet.has(child.id)
      ? deriveTextBoxAttachment(
          child,
          element,
          child.textBoxAttachment.alignment,
          getTextBoxAttachmentPosition(child.textBoxAttachment)
        )
      : child.textBoxAttachment;
    const dependent =
      attachment === child.textBoxAttachment
        ? child
        : { ...child, textBoxAttachment: attachment };
    pending.push(
      resolve
        ? resolveTextBoxAttachment(dependent, element, attachment)
        : dependent
    );
  }

  // With one parent and one child per layer, unreachable components are
  // cycles. Detach all their links in place, independently of drawing order.
  let normalized = candidates.map(
    (element) =>
      resolvedById.get(element.id) || withoutTextBoxAttachment(element)
  );

  if (resolve && previousElements && rebaseIdSet.size === 0) {
    normalized = reflowTextBoxAttachmentStacks(normalized, previousElements);
  }
  return normalized.every((element, index) => element === elements[index])
    ? elements
    : normalized;
};

export const remapTextBoxAttachment = (
  element: Element,
  idMap: Record<string, string>
): Element => {
  if (
    !isTextBoxAttachmentDependent(element) ||
    !isTextBoxAttachment(element.textBoxAttachment)
  ) {
    return element;
  }
  const targetId = idMap[element.textBoxAttachment.targetId];
  if (!targetId) {
    return withoutTextBoxAttachment(element);
  }
  return {
    ...element,
    textBoxAttachment: {
      ...element.textBoxAttachment,
      targetId,
    },
  };
};

export const stripTextBoxAttachment = withoutTextBoxAttachment;
